import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronLeft, ChevronRight, Edit, Edit2, Save, X, Plus, Image, Eye, Upload, RefreshCw, FolderPlus, Pen, FolderX, Sparkles, AlertCircle, CheckCircle, Loader2, Target } from 'lucide-react';
import Logo from '@/components/Logo';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/services/auth/authContext';
import Cookies from 'js-cookie';
import { cn } from '@/lib/utils';
import { getApiUrl } from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { planHasAIImages, getPlanImageLimit } from '@/utils/planUtils';
import apiClient from '@/services/api/client';
import { MenuSearch, SearchFilters } from '@/components/MenuSearch';

// Helper function to sanitize URLs to prevent XSS attacks
const sanitizeImageUrl = (url: string | null | undefined): string => {
  if (!url) return '';
  
  // Remove any whitespace
  const trimmedUrl = String(url).trim();
  
  // Check for dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:'];
  const lowerUrl = trimmedUrl.toLowerCase();
  
  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      console.warn(`Blocked dangerous URL: ${url}`);
      return ''; // Return empty string for dangerous URLs
    }
  }
  
  // Allow relative URLs and http/https URLs
  if (trimmedUrl.startsWith('/') || 
      trimmedUrl.startsWith('http://') || 
      trimmedUrl.startsWith('https://')) {
    return trimmedUrl;
  }
  
  // Add https:// if no protocol specified (for external URLs)
  if (trimmedUrl.includes('.') && !trimmedUrl.includes('://')) {
    return `https://${trimmedUrl}`;
  }
  
  return trimmedUrl;
};

interface MenuItem {
  id: string;
  section_id: string;
  section?: string; // NEW: Track section name for updates
  name: string;
  description: string;
  price: number;
  additional_price?: number;
  price_type?: string;
  category: string;
  dietary_flags: { [key: string]: boolean };  // Changed from string[] to object
  allergens: string[];
  uploaded_image_url?: string | null;
  ai_image_url?: string | null;
  created_at: string;
  updated_at: string;
  isEditing?: boolean;
  is_market_price?: boolean;
  market_price_indicator?: string;
  is_modifier?: boolean;
  imageStatus?: 'none' | 'user' | 'ai' | 'processing';
  aiGenerationEnabled?: boolean;
}

interface MenuSection {
  id: string;
  name: string;
  image_path: string;
  page_number: number;
  section_number: number;
  category: string;
  confidence: number;
  items_count: number;
  isEditing?: boolean;
}

const RestaurantMenuPreview = () => {
  console.log('🚀 RestaurantMenuPreview component mounting...');
  
  const navigate = useNavigate();

  // Wine pricing detection and display helpers
  const shouldShowWinePricing = (item: MenuItem): boolean => {
    return !!(
      item.additional_price && 
      item.additional_price > 0 &&
      (item.price_type === 'wine' || item.price_type === 'beverage')
    );
  };

  const getDisplayPrice = (item: MenuItem, sectionName?: string): string => {
    // Handle market price items first
    if (item.is_market_price && item.market_price_indicator) {
      return item.market_price_indicator;
    }

    // Handle dual pricing (only for beverage sections)
    if (shouldShowWinePricing(item)) {
      const hasGlass = item.price && item.price > 0;
      const hasBottle = item.additional_price && item.additional_price > 0;

      if (hasGlass && hasBottle) {
        return `Glass $${item.price.toFixed(2)} / Bottle $${item.additional_price!.toFixed(2)}`;
      } else if (hasBottle) {
        return `Bottle $${item.additional_price!.toFixed(2)}`;
      } else if (hasGlass) {
        return `Glass $${item.price.toFixed(2)}`;
      } else {
        return 'Price unavailable';
      }
    }

    // Handle regular pricing
    return `$${item.price.toFixed(2)}`;
  };
  
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { user } = useAuth();
  const [showItemPreview, setShowItemPreview] = useState<string | null>(null);
  const [sections, setSections] = useState<MenuSection[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [editingItems, setEditingItems] = useState<Record<string, Partial<MenuItem>>>({});
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [creatingItem, setCreatingItem] = useState(false);
  // Delete functionality disabled in preview mode
  
  // Quick Win mode detection
  const quickWinType = searchParams.get('quickwin');
  const quickWinFilter = searchParams.get('filter');
  const [isQuickWinMode, setIsQuickWinMode] = useState(false);
  const [quickWinCompleted, setQuickWinCompleted] = useState(false);
  
  // Quick Wins feature removed - no longer used
  const [activeQuickWins] = useState<any[]>([]);
  const [showQuickWinProgress] = useState(false);
  
  // Section management state
  const [editingSections, setEditingSections] = useState<Record<string, string>>({});
  const [creatingSection, setCreatingSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    sections: [],
    dietary: [],
    itemType: 'all',
    priceRange: {}
  });
  
  // AI Image Generation state
  const [aiGenerationStatus, setAiGenerationStatus] = useState<Record<string, 'idle' | 'generating' | 'success' | 'error'>>({});
  const [selectedItemsForAI, setSelectedItemsForAI] = useState<Set<string>>(new Set());
  const [sectionGenerationStatus, setSectionGenerationStatus] = useState<Record<string, boolean>>({});
  const [imageQuota, setImageQuota] = useState({ 
    used: 0, 
    limit: 0, 
    remaining: 0 
  });
  const [actualSubscriptionPlan, setActualSubscriptionPlan] = useState<string | null>(null);
  // Delete state not needed in preview mode
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItem, setNewItem] = useState({
    item_name: '',
    item_description: '',
    primary_price: '',
    section: ''
  });
  const [isSeasonalMode, setIsSeasonalMode] = useState(false);
  const [activeSeasonalMenuId, setActiveSeasonalMenuId] = useState<string | null>(null);
  const [hasSeasonalMenus, setHasSeasonalMenus] = useState(false);

  // Use actual subscription plan from state - wait for it to load before using features
  const subscriptionPlan = actualSubscriptionPlan || 'Trial';
  const hasAIImages = actualSubscriptionPlan ? planHasAIImages(actualSubscriptionPlan) : false;
  const monthlyLimit = actualSubscriptionPlan ? getPlanImageLimit(actualSubscriptionPlan) : 0;
  
  console.log('🎫 Subscription details:', {
    plan: subscriptionPlan,
    hasAIImages: hasAIImages,
    monthlyLimit: monthlyLimit
  });

  // Get processing_id from URL params and determine context
  const processingId = searchParams.get('processing_id');
  const urlSeasonalMenuId = searchParams.get('seasonal_menu_id');
  // Enable AI mode if explicitly set OR if in Quick Win photo mode
  const aiMode = searchParams.get('aiMode') === 'true' || (quickWinType === 'photos' && quickWinFilter === 'no-photos');
  
  // Determine preview mode based on source
  const source = searchParams.get('source');
  const isLocationFlow = source === 'location' || searchParams.get('new_location') === 'true';
  const isFrictionlessFlow = source === 'frictionless';
  const isPreSignup = searchParams.get('pre_signup') === 'true';
  
  // Preview modes:
  // 1. Read-only: Pre-signup frictionless flow
  // 2. Edit-only: Post-signup frictionless or location management
  const isReadOnlyMode = isPreSignup;
  const isEditOnlyMode = (isFrictionlessFlow && !isPreSignup) || isLocationFlow;
  const isEditMode = !isReadOnlyMode && !isEditOnlyMode; // Full edit mode (shouldn't happen in preview)
  
  console.log('🔑 Processing ID:', processingId);
  console.log('🎯 Mode:', isReadOnlyMode ? 'READ-ONLY' : isEditOnlyMode ? 'EDIT-ONLY' : 'FULL-EDIT');
  console.log('📍 Source:', source || 'unknown');
  console.log('🏢 Location Flow:', isLocationFlow);
  console.log('🚀 Frictionless Flow:', isFrictionlessFlow);
  console.log('🤖 AI Mode:', aiMode);
  console.log('🔗 Current URL:', window.location.href);

  // Filter items based on Quick Win mode
  const getFilteredItems = (items: MenuItem[]) => {
    let filtered = items;

    // Apply Quick Win filters first if in Quick Win mode
    if (isQuickWinMode && quickWinFilter) {
      switch (quickWinFilter) {
        case 'no-photos':
          filtered = filtered.filter(item => !item.uploaded_image_url && !item.ai_image_url);
          break;
        case 'no-description':
          filtered = filtered.filter(item => !item.description || item.description.trim() === '');
          break;
      }
    }
    
    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(term) ||
        item.description?.toLowerCase().includes(term) ||
        item.price.toString().includes(term) ||
        (item.additional_price && item.additional_price.toString().includes(term))
      );
    }
    
    // Apply filters
    if (searchFilters.sections.length > 0) {
      filtered = filtered.filter(item => searchFilters.sections.includes(item.section_id));
    }
    
    if (searchFilters.dietary.length > 0) {
      filtered = filtered.filter(item => {
        // dietary_flags is a JSONB object like {"vegan": true, "vegetarian": false}
        if (!item.dietary_flags || typeof item.dietary_flags !== 'object') return false;
        
        // Use AND logic - item must match ALL selected dietary filters
        return searchFilters.dietary.every(dietary => 
          item.dietary_flags[dietary] === true
        );
      });
    }
    
    if (searchFilters.itemType !== 'all') {
      filtered = filtered.filter(item => {
        const isBeverage = item.category?.toLowerCase().includes('beverage') || 
                          item.category?.toLowerCase().includes('drink') ||
                          item.category?.toLowerCase().includes('wine') ||
                          item.category?.toLowerCase().includes('cocktail') ||
                          item.category?.toLowerCase().includes('beer');
        return searchFilters.itemType === 'beverage' ? isBeverage : !isBeverage;
      });
    }
    
    return filtered;
  };

  // Filter sections to only show those with items matching the filter
  const getFilteredSections = (sections: MenuSection[], items: MenuItem[]) => {
    // Always filter sections based on which ones have filtered items
    const filteredItems = getFilteredItems(items);
    const sectionsWithFilteredItems = new Set(filteredItems.map(item => item.section_id));
    
    return sections.filter(section => sectionsWithFilteredItems.has(section.id));
  };
  
  // Handle search
  const handleSearch = useCallback((term: string, filters: SearchFilters) => {
    setSearchTerm(term);
    setSearchFilters(filters);
  }, []);

  // API Functions
  const createMenuItem = async (sectionId: string, sectionName: string, itemData: { 
    name: string; 
    description: string; 
    price: number;
    is_market_price?: boolean;
    market_price_indicator?: string;
    price_type?: string;
    additional_price?: number;
  }) => {
    try {
      const token = Cookies.get('auth_token'); // Get token from cookies

      // Use processingId from URL params as menu_id
      const menuId = processingId;
      
      const response = await apiClient.post(`/menus/${menuId}/items`, {
        name: itemData.name,
        description: itemData.description,
        price: itemData.price,
        section_id: sectionId,
        section_name: sectionName,
        dietary_flags: {},  // Changed from [] to {}
        allergens: [],
        is_market_price: itemData.is_market_price || false,
        market_price_indicator: itemData.market_price_indicator,
        price_type: itemData.price_type || 'fixed',
        additional_price: itemData.additional_price
      });

      return response.data;
    } catch (error) {
      console.error('Error creating menu item:', error);
      throw error;
    }
  };

  const deleteMenuItem = async (menuItemId: string) => {
    try {
      // Use processingId from URL params as menu_id
      const menuId = processingId;
      
      const result = await apiClient.delete(`/menus/${menuId}/items/${menuItemId}`);
      
      console.log('✅ Menu item deleted successfully with Redis sync:', result);
      return result.data;  // Return the data portion of axios response
    } catch (error) {
      console.error('Error deleting menu item:', error);
      throw error;
    }
  };

  const updateMenuItem = async (menuItemId: string, itemData: Partial<MenuItem>) => {
    try {
      // Use processingId from URL params as menu_id
      const menuId = processingId;
      
      const result = await apiClient.put(`/menus/${menuId}/items/${menuItemId}`, {
        name: itemData.name,
        description: itemData.description,
        section: itemData.section, // NEW: Include section for re-categorization
        price: itemData.price,
        additional_price: itemData.additional_price,
        price_type: itemData.price_type,
        dietary_flags: itemData.dietary_flags || {},
        allergens: itemData.allergens || [],
        is_market_price: itemData.is_market_price || false,
        market_price_indicator: itemData.market_price_indicator,
        is_popular: itemData.is_popular || false,
        chef_reccom: itemData.chef_reccom || false,
        is_hero_carousel: itemData.is_hero_carousel || false,
        hero_display_order: itemData.hero_display_order || null,
        spice_adjustable: itemData.spice_adjustable || false,
        cuisine_type: itemData.cuisine_type || ''
      });

      console.log('✅ Menu item updated successfully with Redis sync:', result);
      return result.data;  // Return the data portion of axios response
    } catch (error) {
      console.error('Error updating menu item:', error);
      throw error;
    }
  };

  // Fetch restaurant profile to get actual subscription plan
  // Set up Quick Win mode
  useEffect(() => {
    if (quickWinType && quickWinFilter) {
      setIsQuickWinMode(true);
      console.log('🎯 Quick Win Mode Active:', quickWinType, quickWinFilter);
    }
  }, [quickWinType, quickWinFilter]);
  
  // Quick Wins feature removed - skip fetching
  useEffect(() => {
    const fetchActiveQuickWins = async () => {
      try {
        // Quick wins feature is disabled - skip API call
        // const response = await apiClient.get('/quick-wins');
        // const wins = response.data.wins || [];
        // 
        // // Filter for relevant Quick Wins (photos and descriptions)
        // const relevantWins = wins.filter((win: any) => 
        //   !win.is_completed && 
        //   (win.category === 'photos' || win.category === 'descriptions')
        // );
        
        setActiveQuickWins([]);
        setShowQuickWinProgress(false);
      } catch (error) {
        // Quick Wins feature removed
      }
    };
    
    fetchActiveQuickWins();
  }, [isQuickWinMode]);
  
  // Quick Wins feature removed - skip checking
  useEffect(() => {
    const checkQuickWinCompletion = async () => {
      if (activeQuickWins.length === 0 || !menuItems.length) return;
      
      for (const win of activeQuickWins) {
        const isPhotoWin = win.category === 'photos';
        const incompleteItems = menuItems.filter(item => 
          isPhotoWin 
            ? item.imageStatus === 'none'
            : !item.description || item.description.trim() === ''
        );
        
        // If all items are complete for this Quick Win
        if (incompleteItems.length === 0) {
          try {
            // Auto-complete the Quick Win
            // Quick wins feature is disabled
            // await apiClient.put(`/quick-wins/${win.id}/complete`);
            
            toast({
              title: "🎉 Quick Win Completed!",
              description: `${win.title} has been automatically completed.`,
            });
            
            // Quick wins feature removed
            // const response = await apiClient.get('/quick-wins');
            // const wins = response.data.wins || [];
            // const relevantWins = wins.filter((w: any) => 
            //   !w.is_completed && 
            //   (w.category === 'photos' || w.category === 'descriptions')
            // );
            setActiveQuickWins([]);
          } catch (error) {
            console.error('Failed to auto-complete Quick Win:', error);
          }
        }
      }
    };
    
    // Debounce the check to avoid too many API calls
    const timer = setTimeout(checkQuickWinCompletion, 1000);
    return () => clearTimeout(timer);
  }, [menuItems, activeQuickWins]);

  useEffect(() => {
    const fetchRestaurantProfile = async () => {
      try {
        const response = await apiClient.get('/restaurants/profile');
        if (response.data?.subscription_plan) {
          console.log('📋 Actual subscription plan:', response.data.subscription_plan);
          setActualSubscriptionPlan(response.data.subscription_plan);
        }
      } catch (error) {
        console.error('Failed to fetch restaurant profile:', error);
      }
    };
    
    fetchRestaurantProfile();
  }, []);

  // Fetch active seasonal menu if in AI mode
  useEffect(() => {
    const fetchActiveSeasonalMenu = async () => {
      console.log('🎯 AI Mode check:', { aiMode, processingId });
      if (!aiMode) {
        console.log('❌ Not in AI mode, skipping seasonal menu fetch');
        return;  // Only fetch seasonal menus in AI mode
      }
      
      console.log('✅ In AI mode, fetching seasonal menus...');
      try {
        // Use apiClient instead of fetch for proper authentication
        const response = await apiClient.get('/seasonal-menus');
        console.log('📡 Seasonal menu response:', response);
        
        if (response.data) {
          const data = response.data;
          console.log('🔍 All seasonal menus:', data);
          
          // Use proper backend response structure - data is an array of seasonal menus
          const seasonalMenus = Array.isArray(data) ? data : data.seasonal_menus || [];
          
          if (seasonalMenus.length > 0) {
            // Set flag that seasonal menus exist
            setHasSeasonalMenus(true);
            console.log('✅ Seasonal menus detected, showing toggle');
            
            // Find the most recently created active seasonal menu
            const activeMenu = seasonalMenus
              .filter((menu: any) => menu.is_active)
              .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0] 
              || seasonalMenus[0]; // Fallback to first menu if none are active
            
            if (activeMenu) {
              setActiveSeasonalMenuId(activeMenu.seasonal_menu_id.toString());
              console.log('🍁 Found seasonal menu:', activeMenu.menu_name, activeMenu.seasonal_menu_id);
            }
          } else {
            setHasSeasonalMenus(false);
            setIsSeasonalMode(false); // Reset to regular menu if no seasonal menus
            setActiveSeasonalMenuId(null);
            console.log('⚠️ No seasonal menus found');
          }
        } else {
          console.log('❌ No data in response');
          setHasSeasonalMenus(false);
          setIsSeasonalMode(false);
          setActiveSeasonalMenuId(null);
        }
      } catch (error) {
        console.error('❌ Failed to fetch seasonal menus:', error);
        setHasSeasonalMenus(false);
        setIsSeasonalMode(false); // Reset to regular menu on error
        setActiveSeasonalMenuId(null);
      }
    };
    
    fetchActiveSeasonalMenu();
  }, [aiMode]);

  useEffect(() => {
    const fetchMenuData = async () => {
      // Check if we have a seasonal menu ID from URL params (coming from special menu creation flow)
      const isSpecialMenuFromUrl = !!urlSeasonalMenuId;

      // Determine which menu ID to use
      const menuId = isSpecialMenuFromUrl
        ? processingId // Use menu_id from processing for special menus
        : (isSeasonalMode && activeSeasonalMenuId ? activeSeasonalMenuId : processingId);

      if (!menuId) {
        console.error('❌ No menu ID provided');
        setError('No menu ID provided. Please navigate from the dashboard.');
        setLoading(false);
        return;
      }
      
      // Prevent multiple fetches
      if (isInitialized) {
        console.log('⚠️ Menu data already initialized, skipping fetch');
        return;
      }
      
      try {
        setLoading(true);
        const token = Cookies.get('auth_token'); // Get token from cookies
        
        console.log('🔍 Fetching menu data for menuId:', menuId,
          isSpecialMenuFromUrl ? '(Special Menu from URL)' :
          isSeasonalMode ? '(Seasonal)' : '(Regular)');

        // For special menus from URL, use seasonal endpoint with the seasonal_menu_id
        const apiUrl = isSpecialMenuFromUrl
          ? `/seasonal-menus/${urlSeasonalMenuId}/items`
          : (isSeasonalMode ? `/seasonal-menus/${menuId}/items` : `/menus/${menuId}/items`);
        console.log('🔗 API URL:', apiUrl);
        
        const response = await apiClient.get(apiUrl);
        const data = response.data; // apiClient returns {data: ...}
        console.log('📡 Response data:', data);

        // Validate response data
        if (!data || (!data.items && !data.sections)) {
          console.error('Invalid response data:', data);
          throw new Error('Invalid menu data received from server');
        }

        console.log('✅ API Response data:', {
          sectionsCount: data.sections?.length,
          itemsCount: data.items?.length,
          totalItems: data.total_items || data.total
        });
        
        // Process sections based on data format
        let sections: MenuSection[];
        let itemsToProcess: any[] = [];

        if (isSeasonalMode || isSpecialMenuFromUrl) {
          // Seasonal menu returns {items: [...], total: n}
          itemsToProcess = data.items || [];

          // Transform seasonal menu format to regular format
          sections = itemsToProcess.reduce((acc: MenuSection[], item: any) => {
            const existingSection = acc.find(s => s.name === item.section);
            if (!existingSection) {
              acc.push({
                id: item.section,
                name: item.section,
                image_path: '',
                page_number: 0,
                section_number: acc.length + 1,
                category: 'other',
                confidence: 1,
                items_count: 1
              });
            } else {
              existingSection.items_count++;
            }
            return acc;
          }, []);
          setSections(sections);
        } else {
          // Regular menu returns {sections: [...], items: [...]}
          sections = data.sections || [];
          itemsToProcess = data.items || [];
          setSections(sections);
        }

        // Process menu items to add image status
        const processedItems = itemsToProcess.map((item: any) => {
          // Ensure ID is always a string for consistency
          // This fixes the editing issue where numeric IDs didn't match string keys in editingItems state
          const itemId = String(item.id || item.menu_item_id);

          // Transform seasonal menu item format to regular format if needed
          const transformedItem = (isSeasonalMode || isSpecialMenuFromUrl) ? {
            id: itemId,
            name: item.item_name,
            description: item.item_description,
            price: item.primary_price,
            section_id: item.section,
            category: item.section?.toLowerCase(),
            dietary_flags: item.dietary_flags || {},
            allergens: [],
            uploaded_image_url: item.uploaded_image_url,
            ai_image_url: item.ai_image_url,
            created_at: item.created_at || new Date().toISOString(),
            updated_at: item.updated_at || new Date().toISOString(),
            is_market_price: item.is_market_price,
            market_price_indicator: item.market_price_indicator
          } : {
            ...item,
            id: itemId  // Ensure regular items also have string IDs
          };
          // Determine image status - user upload takes priority over AI
          const status = transformedItem.uploaded_image_url ? 'user' : 
                        transformedItem.ai_image_url ? 'ai' : 
                        'none';
          
          console.log('🖼️ Processing item image status:', {
            itemId: transformedItem.id,
            itemName: transformedItem.name,
            uploaded_image_url: transformedItem.uploaded_image_url,
            ai_image_url: transformedItem.ai_image_url,
            assignedStatus: status
          });
          
          return {
            ...transformedItem,
            imageStatus: status as 'none' | 'user' | 'ai',
            aiGenerationEnabled: !transformedItem.uploaded_image_url // AI generation disabled if user uploaded image
          };
        });
        
        setMenuItems(processedItems);
        
        // Default to first section
        if (sections.length > 0) {
          setSelectedSectionId(sections[0].id);
        }
        
        // Mark as initialized
        setIsInitialized(true);
      } catch (err) {
        console.error('❌ Error fetching menu data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load menu data');
      } finally {
        setLoading(false);
      }
    };

    fetchMenuData();
  }, [processingId, isSeasonalMode, activeSeasonalMenuId, isInitialized, urlSeasonalMenuId]);
  
  // Debug selectedItemsForAI changes
  useEffect(() => {
    console.log('🎯 Selected items changed:', {
      count: selectedItemsForAI.size,
      items: Array.from(selectedItemsForAI)
    });
  }, [selectedItemsForAI]);
  
  // Debug imageQuota changes
  useEffect(() => {
    console.log('💰 Image quota changed:', imageQuota);
  }, [imageQuota]);
  
  // Fetch AI image generation quota
  const fetchImageQuota = useCallback(async () => {
    console.log('📊 Fetching image quota...');
    try {
      const response = await apiClient.get('/images/usage');
      const data = response.data; // apiClient returns {data: ...}
      console.log('📊 Quota data received:', data);
      
      if (data) {
        console.log('📊 Quota data received:', data);
        const newQuota = {
          used: data.used || 0,
          limit: data.limit || 0,
          remaining: data.remaining || 0
        };
        console.log('📊 About to update imageQuota state with:', newQuota);
        setImageQuota(newQuota);
        console.log('📊 ✅ Image quota state updated:', newQuota);
      }
    } catch (error) {
      console.error('📊 Failed to fetch image quota:', error);
    }
  }, []);
  
  // Fetch quota on mount - only when subscription plan is loaded
  useEffect(() => {
    // Don't run until subscription plan is loaded from API
    if (!actualSubscriptionPlan) {
      console.log('🎫 Subscription plan not loaded yet, skipping image quota fetch');
      return;
    }

    console.log('🎫 useEffect running - Subscription plan loaded:', actualSubscriptionPlan);
    console.log('🎫 hasAIImages:', hasAIImages, 'aiMode:', aiMode, 'isEditMode:', isEditMode);

    if (hasAIImages) {
      console.log('🎫 User has AI images, fetching quota...');
      // Add a small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        console.log('🎫 Timer fired, calling fetchImageQuota...');
        fetchImageQuota();
      }, 500);

      return () => clearTimeout(timer);
    } else {
      console.log('🎫 User does NOT have AI images (Virtual Host or no subscription), skipping quota fetch');
    }
  }, [actualSubscriptionPlan, hasAIImages]); // Re-run when plan loads
  
  // Generate AI image for a single item
  const generateAIImage = async (menuItemId: string) => {
    const token = Cookies.get('auth_token');
    
    setAiGenerationStatus(prev => ({ ...prev, [menuItemId]: 'generating' }));
    
    try {
      const result = await apiClient.post(`/images/generate/${menuItemId}`);
      
      console.log('🖼️ AI Image Generation Result:', {
        menuItemId,
        imageUrl: result.image_url,
        fullResult: result
      });
      
      // Update menu item with AI image URL
      setMenuItems(prev => prev.map(item => {
        if (item.id === menuItemId) {
          const updatedItem = { 
            ...item, 
            ai_image_url: result.data.image_url, 
            imageStatus: 'ai' as const 
          };
          console.log('🖼️ Updated item:', {
            id: item.id,
            name: item.name,
            ai_image_url: updatedItem.ai_image_url,
            imageStatus: updatedItem.imageStatus
          });
          return updatedItem;
        }
        return item;
      }));
      
      setAiGenerationStatus(prev => ({ ...prev, [menuItemId]: 'success' }));
      
      // Refresh quota
      fetchImageQuota();
      
      toast({
        title: "AI Image Generated",
        description: "Successfully generated image",
      });
      
      // Optionally refresh the specific item from backend to ensure we have the correct URL
      // This is helpful if the backend URL format is different from what we receive
      if (processingId) {
        try {
          const token = Cookies.get('auth_token');
          const itemResponse = await apiClient.get(`/menus/${processingId}/items`);
          const data = itemResponse.data; // apiClient returns {data: ...}
          
          if (data && data.items) {
            const updatedItem = data.items.find((i: MenuItem) => i.id === menuItemId);
            if (updatedItem) {
              console.log('🖼️ Refreshed item from backend:', updatedItem);
              setMenuItems(prev => prev.map(item => 
                item.id === menuItemId ? { ...item, ...updatedItem, imageStatus: 'ai' as const } : item
              ));
            }
          }
        } catch (error) {
          console.error('Failed to refresh item data:', error);
        }
      }
      
    } catch (error) {
      setAiGenerationStatus(prev => ({ ...prev, [menuItemId]: 'error' }));
      toast({
        title: "Generation Failed",
        description: error instanceof Error ? error.message : "Failed to generate AI image",
        variant: "destructive",
      });
    }
  };
  
  // Generate AI images for selected items or entire section
  const generateSectionImages = async (sectionId: string) => {
    let itemsToGenerate: MenuItem[] = [];
    
    // Check if there are selected items in this section
    const selectedInSection = menuItems.filter(item => 
      item.section_id === sectionId && 
      selectedItemsForAI.has(String(item.id)) &&
      item.imageStatus === 'none'
    );
    
    if (selectedInSection.length > 0) {
      // Use selected items
      itemsToGenerate = selectedInSection;
    } else {
      // No selection, get all items without images
      itemsToGenerate = menuItems.filter(item => 
        item.section_id === sectionId && 
        item.imageStatus === 'none' &&
        !item.uploaded_image_url
      );
    }
    
    if (itemsToGenerate.length === 0) {
      toast({
        title: "No items to generate",
        description: selectedInSection.length > 0 
          ? "Selected items already have images" 
          : "All items in this section already have images",
      });
      return;
    }
    
    if (imageQuota.remaining < itemsToGenerate.length) {
      toast({
        title: "Insufficient quota",
        description: `You need ${itemsToGenerate.length} images but only have ${imageQuota.remaining} remaining`,
        variant: "destructive",
      });
      return;
    }
    
    setSectionGenerationStatus(prev => ({ ...prev, [sectionId]: true }));
    
    // Generate images sequentially to avoid rate limiting
    for (const item of itemsToGenerate) {
      await generateAIImage(item.id);
      // Add a small delay between requests
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    // Clear selection after generation
    setSelectedItemsForAI(new Set());
    
    setSectionGenerationStatus(prev => ({ ...prev, [sectionId]: false }));
  };

  // Create section name mapping for display
  const getSectionName = (sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    return section ? section.name : 'Unknown';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] flex items-center justify-center">
        <div className="text-white text-xl">Loading menu data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] flex items-center justify-center">
        <div className="text-white text-xl">Error: {error}</div>
      </div>
    );
  }

  // Apply Quick Win filters
  const filteredItems = getFilteredItems(menuItems);
  const filteredSections = getFilteredSections(sections, menuItems);

  const sectionsWithItems = filteredSections
    .map(section => {
      const sectionItems = filteredItems.filter(item => item.section_id === section.id);
      return {
        ...section,
        name: getSectionName(section.id),
        items: sectionItems
      };
    })
    .sort((a, b) => {
      // Sort by menu order: food sections first, then beverages
      const foodCategories = ['appetizers', 'salads', 'soups', 'entrees', 'burgers', 'sandwiches', 'pasta', 'seafood', 'steaks', 'desserts', 'kids', 'sides'];
      const beverageCategories = ['beverages'];
      
      const aIsFood = foodCategories.includes(a.category);
      const bIsFood = foodCategories.includes(b.category);
      const aIsBeverage = beverageCategories.includes(a.category);
      const bIsBeverage = beverageCategories.includes(b.category);
      
      // Food sections come first
      if (aIsFood && !bIsFood) return -1;
      if (!aIsFood && bIsFood) return 1;
      
      // Within same category, maintain original order
      return a.section_number - b.section_number;
    });

  // Get currently selected section
  const selectedSection = sectionsWithItems.find(section => section.id === selectedSectionId) || sectionsWithItems[0];

  const handleEdit = (itemId: string) => {
    const item = menuItems.find(i => i.id === itemId);
    if (item) {
      setEditingItems({
        ...editingItems,
        [itemId]: { ...item }
      });
    }
  };

  const handleSave = async (itemId: string) => {
    const editedItem = editingItems[itemId];
    if (!editedItem) return;

    // Validation
    if (!editedItem.name || !editedItem.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Item name is required",
        variant: "destructive",
      });
      return;
    }

    // Skip price validation for market price items
    if (!editedItem.is_market_price) {
      if (!editedItem.price || editedItem.price <= 0) {
        toast({
          title: "Validation Error", 
          description: isWineItem(editedItem) ? "Valid glass price is required" : "Valid price is required",
          variant: "destructive",
        });
        return;
      }
    } else {
      // Market price validation
      if (!editedItem.market_price_indicator || !editedItem.market_price_indicator.trim()) {
        toast({
          title: "Validation Error",
          description: "Market price indicator is required (e.g., 'MKT', 'Market Price')",
          variant: "destructive",
        });
        return;
      }
    }

    // Wine item validation
    if (isWineItem(editedItem)) {
      if (!editedItem.additional_price || editedItem.additional_price <= 0) {
        toast({
          title: "Validation Error", 
          description: "Valid bottle price is required for wine items",
          variant: "destructive",
        });
        return;
      }
      if (editedItem.additional_price <= editedItem.price) {
        toast({
          title: "Validation Error", 
          description: "Bottle price should be higher than glass price",
          variant: "destructive",
        });
        return;
      }
    }

    setCreatingItem(true);

    try {
      if (itemId.startsWith('new-')) {
        // This is a new item - create it via API
        const section = sections.find(s => s.id === editedItem.section_id);
        if (!section) {
          throw new Error('Section not found');
        }

        const result = await createMenuItem(
          editedItem.section_id!,
          section.name,
          {
            name: editedItem.name.trim(),
            description: editedItem.description || '',
            price: editedItem.price || 0,
            is_market_price: editedItem.is_market_price || false,
            market_price_indicator: editedItem.market_price_indicator,
            price_type: editedItem.price_type || 'fixed',
            additional_price: editedItem.additional_price
          }
        );

        console.log('📝 Create result:', result);
        if (result.success && result.item) {
          // Replace the temporary item with the real item from the API
          setMenuItems(menuItems.map(item => 
            item.id === itemId ? {
              ...result.item,
              // Keep the isEditing flag as false since we just saved
              isEditing: false
            } : item
          ));

          toast({
            title: "Item created",
            description: `Successfully created "${editedItem.name}"`,
          });

          // Scroll to the newly created item with its new ID
          setTimeout(() => {
            const newItemElement = document.getElementById(`menu-item-${result.item.id}`);
            if (newItemElement) {
              newItemElement.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center',
                inline: 'nearest'
              });
            }
          }, 100);
        }
      } else {
        // This is an existing item - update it via API
        const result = await updateMenuItem(itemId, editedItem);

        console.log('📝 Update result:', result);
        if (result && result.success && result.item) {
          // Update the item in local state with the actual data returned from API
          setMenuItems(prevItems => prevItems.map(item => 
            item.id === itemId ? {
              ...item, // Keep original item structure and IDs
              // Update with the actual data from API response
              name: result.item.name,
              description: result.item.description,
              price: result.item.price,
              additional_price: result.item.additional_price,
              price_type: result.item.price_type,
              is_market_price: result.item.is_market_price,
              market_price_indicator: result.item.market_price_indicator,
              dietary_flags: result.item.dietary_flags || item.dietary_flags,
              ai_image_url: result.item.ai_image_url,
              uploaded_image_url: result.item.uploaded_image_url,
              updated_at: result.item.updated_at || new Date().toISOString(),
              isEditing: false
            } : item
          ));

          toast({
            title: "Item updated",
            description: `Successfully updated "${editedItem.name}"`,
          });
        }
      }

      // Remove from editing state
      setEditingItems(prevEditingItems => {
        const newEditingItems = { ...prevEditingItems };
        delete newEditingItems[itemId];
        return newEditingItems;
      });

    } catch (error) {
      toast({
        title: "Save failed",
        description: error instanceof Error ? error.message : "Failed to save menu item",
        variant: "destructive",
      });
    } finally {
      setCreatingItem(false);
    }
  };

  const handleCancel = (itemId: string) => {
    const newEditingItems = { ...editingItems };
    delete newEditingItems[itemId];
    setEditingItems(newEditingItems);
  };

  const handleFieldChange = (itemId: string, field: keyof MenuItem, value: string | number) => {
    setEditingItems({
      ...editingItems,
      [itemId]: {
        ...editingItems[itemId],
        [field]: value
      }
    });
  };

  // Delete functionality disabled in preview mode

  // Delete functionality disabled in preview mode

  const handleWineToggle = (itemId: string, isWineItem: boolean) => {
    const currentItem = editingItems[itemId];
    if (isWineItem) {
      // Convert to wine item - set default bottle price if not already set
      setEditingItems({
        ...editingItems,
        [itemId]: {
          ...currentItem,
          price_type: 'beverage',
          additional_price: currentItem?.additional_price || (currentItem?.price ? currentItem.price * 3 : 0), // Default bottle price (3x glass)
          is_market_price: false, // Clear market price when enabling wine pricing
          market_price_indicator: undefined
        }
      });
    } else {
      // Convert to regular item - remove wine pricing
      setEditingItems({
        ...editingItems,
        [itemId]: {
          ...currentItem,
          price_type: 'fixed',
          additional_price: undefined
        }
      });
    }
  };

  const handleMarketPriceToggle = (itemId: string, isMarketPrice: boolean) => {
    const currentItem = editingItems[itemId];
    const originalItem = menuItems.find(item => item.id === itemId);
    
    if (isMarketPrice) {
      // Convert to market price item
      setEditingItems({
        ...editingItems,
        [itemId]: {
          ...currentItem,
          is_market_price: true,
          market_price_indicator: currentItem?.market_price_indicator || 'MKT',
          price_type: 'market',
          additional_price: undefined, // Clear wine pricing when enabling market price
          price: 0 // Set price to 0 for market items
        }
      });
    } else {
      // Convert to regular item - remove market pricing and restore original price
      const restorePrice = originalItem?.price && originalItem.price > 0 ? originalItem.price : 10.00;
      setEditingItems({
        ...editingItems,
        [itemId]: {
          ...currentItem,
          is_market_price: false,
          market_price_indicator: undefined,
          price_type: 'fixed',
          price: restorePrice // Restore original price or set default
        }
      });
    }
  };

  // Simple section detection - only beverage sections get dual pricing
  const isBeverageSection = (sectionName: string): boolean => {
    const beverageSections = ['WINES', 'COCKTAILS', 'BEER', 'BEVERAGES', 'HANDCRAFTED COCKTAILS', 'HANDCRAFTED MARTINIS', 'REDS', 'WHITES'];
    return beverageSections.some(section => 
      sectionName.toUpperCase().includes(section)
    );
  };

  const isWineItem = (item: Partial<MenuItem>): boolean => {
    return !!(
      (item.additional_price && item.additional_price > 0) ||
      item.price_type === 'wine' || 
      item.price_type === 'beverage'
    );
  };

  const handleDelete = async (itemId: string) => {
    console.log('🗑️ handleDelete called with itemId:', itemId);
    
    // Check if this is a new item (not yet saved to backend)
    if (itemId.startsWith('new-')) {
      console.log('🗑️ Removing unsaved new item locally');
      // Just remove from local state
      setMenuItems(menuItems.filter(item => item.id !== itemId));
      setEditingItems(prev => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
      toast({
        title: "Item removed",
        description: "New item removed from menu",
      });
      return;
    }

    // For saved items, call the API
    setDeletingItems(prev => new Set(prev.add(itemId)));
    
    try {
      await deleteMenuItem(itemId);
      
      // Remove from local state on success
      setMenuItems(menuItems.filter(item => item.id !== itemId));
      setEditingItems(prev => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
      
      toast({
        title: "Item deleted",
        description: "Menu item has been successfully deleted",
      });
    } catch (error) {
      console.error('🗑️ Delete failed:', error);
      toast({
        title: "Delete failed",
        description: error instanceof Error ? error.message : "Failed to delete menu item",
        variant: "destructive",
      });
    } finally {
      setDeletingItems(prev => {
        const updated = new Set(prev);
        updated.delete(itemId);
        return updated;
      });
    }
  };

  const handleAddItem = (sectionId: string) => {
    console.log('🎯 handleAddItem called with sectionId:', sectionId);
    const section = sections.find(s => s.id === sectionId);
    
    // Set the section for the new item and open the modal
    setNewItem({
      item_name: '',
      item_description: '',
      primary_price: '10.00',
      section: section?.name || ''
    });
    setShowAddModal(true);
    
    // Ensure we're viewing the section where the item will be added
    setSelectedSectionId(sectionId);
  };

  const handleCreateItem = async () => {
    if (!newItem.item_name || !newItem.primary_price || !newItem.section) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    setCreatingItem(true);
    try {
      const section = sections.find(s => s.name === newItem.section);
      if (!section) {
        throw new Error('Section not found');
      }

      const result = await createMenuItem(
        section.id,
        newItem.section,
        {
          name: newItem.item_name.trim(),
          description: newItem.item_description.trim(),
          price: parseFloat(newItem.primary_price),
          is_market_price: false,
          price_type: 'fixed'
        }
      );

      console.log('📝 Create result:', result);
      if (result.success && result.item) {
        // Add the new item to menuItems
        const newMenuItem: MenuItem = {
          id: String(result.item.id),
          section_id: section.id,
          name: result.item.name,
          description: result.item.description || '',
          price: result.item.price,
          category: result.item.category || 'other',
          dietary_flags: result.item.dietary_flags || {},
          allergens: result.item.allergens || [],
          created_at: result.item.created_at,
          updated_at: result.item.updated_at,
          uploaded_image_url: null,
          price_type: 'fixed',
          is_market_price: false,
          imageStatus: 'none'
        };

        setMenuItems(prev => [...prev, newMenuItem]);

        toast({
          title: 'Success',
          description: 'Item added successfully'
        });

        // Reset form and close modal
        setNewItem({
          item_name: '',
          item_description: '',
          primary_price: '10.00',
          section: ''
        });
        setShowAddModal(false);
        
        // Scroll to the newly created item
        setTimeout(() => {
          const newItemElement = document.getElementById(`menu-item-${result.item.id}`);
          if (newItemElement) {
            newItemElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            });
          }
        }, 150);
      }
    } catch (error) {
      console.error('Error adding item:', error);
      toast({
        title: 'Error',
        description: 'Failed to add item',
        variant: 'destructive'
      });
    } finally {
      setCreatingItem(false);
    }
  };

  // Section Management Functions
  const handleCreateSection = async () => {
    if (!newSectionName.trim()) {
      toast({
        title: "Section name required",
        description: "Please enter a section name",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await apiClient.post(`/menus/${processingId}/sections`, {
        name: newSectionName.trim(),
        category: 'other'
      });
      console.log('✅ Section created with Redis sync:', result);
      
      if (result.data.success && result.data.section) {
        // Add new section to state
        setSections([...sections, result.data.section]);
        setNewSectionName('');
        setCreatingSection(false);
        
        toast({
          title: "Section created",
          description: `Successfully created section "${result.data.section.name}"`,
        });
      }
    } catch (error) {
      toast({
        title: "Failed to create section",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    }
  };

  const handleStartEditSection = (sectionId: string, currentName: string) => {
    setEditingSections({
      ...editingSections,
      [sectionId]: currentName
    });
  };

  const handleSaveSection = async (sectionId: string) => {
    const newName = editingSections[sectionId];
    if (!newName || !newName.trim()) {
      toast({
        title: "Section name required",
        description: "Section name cannot be empty",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await apiClient.put(`/menus/${processingId}/sections/${sectionId}`, {
        name: newName.trim()
      });
      console.log('✅ Section updated with Redis sync:', result);
      
      if (result.data.success) {
        // Update section name in state
        setSections(sections.map(section => 
          section.id === sectionId 
            ? { ...section, name: newName.trim() }
            : section
        ));
        
        // Update section name in all items of this section
        setMenuItems(menuItems.map(item =>
          item.section_id === sectionId
            ? { ...item, section_id: sectionId }  // Keep same section_id but trigger re-render
            : item
        ));
        
        // Clear editing state
        const newEditingSections = { ...editingSections };
        delete newEditingSections[sectionId];
        setEditingSections(newEditingSections);
        
        toast({
          title: "Section updated",
          description: `Successfully renamed section to "${newName.trim()}"`,
        });
      }
    } catch (error) {
      toast({
        title: "Failed to update section",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    }
  };

  const handleCancelEditSection = (sectionId: string) => {
    const newEditingSections = { ...editingSections };
    delete newEditingSections[sectionId];
    setEditingSections(newEditingSections);
  };

  const handleDeleteSection = async (sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    const itemsInSection = menuItems.filter(item => item.section_id === sectionId);
    
    if (itemsInSection.length > 0) {
      toast({
        title: "Cannot delete section",
        description: `Section "${section?.name}" contains ${itemsInSection.length} items. Move or delete items first.`,
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await apiClient.delete(`/menus/${processingId}/sections/${sectionId}`);
      console.log('✅ Section deleted with Redis sync:', result);
      
      if (result.data.success) {
        // Remove section from state
        setSections(sections.filter(s => s.id !== sectionId));
        
        // If this was the selected section, switch to first available section
        if (selectedSectionId === sectionId) {
          const remainingSections = sections.filter(s => s.id !== sectionId);
          setSelectedSectionId(remainingSections.length > 0 ? remainingSections[0].id : null);
        }
        
        toast({
          title: "Section deleted",
          description: `Successfully deleted section "${section?.name}"`,
        });
      }
    } catch (error) {
      toast({
        title: "Failed to delete section",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    }
  };

  const handleClearImage = async (itemId: string) => {
    try {
      // Update local state immediately for better UX
      setMenuItems(prev => prev.map(item =>
        item.id === itemId
          ? { 
              ...item, 
              uploaded_image_url: null,
              ai_image_url: null,
              imageStatus: 'none' as const,
              aiGenerationEnabled: true // Re-enable AI generation
            }
          : item
      ));
      
      // Call backend to clear the image
      const token = Cookies.get('auth_token');
      
      const result = await apiClient.post(`/menus/${processingId}/items/${itemId}/clear-image`, {});
      console.log('✅ Image cleared with Redis sync:', result);
      
      toast({
        title: "Image Cleared",
        description: "The image has been removed successfully",
      });
      
    } catch (error) {
      console.error('Failed to clear image:', error);
      
      // Revert on error
      const originalItem = menuItems.find(item => item.id === itemId);
      if (originalItem) {
        setMenuItems(prev => prev.map(item =>
          item.id === itemId ? originalItem : item
        ));
      }
      
      toast({
        title: "Clear Failed",
        description: "Failed to remove image. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleImageUpload = async (itemId: string, file: File) => {
    try {
      // First, update the UI optimistically
      const reader = new FileReader();
      reader.onload = async (e) => {
        if (e.target?.result) {
          // Update local state immediately for better UX
          setMenuItems(menuItems.map(item =>
            item.id === itemId
              ? { 
                  ...item, 
                  uploaded_image_url: e.target.result as string,
                  ai_image_url: null, // Clear AI image when user uploads
                  imageStatus: 'user' as const, // Change status to user uploaded
                  aiGenerationEnabled: false // Disable AI generation for user uploaded images
                }
              : item
          ));
          
          // Now upload to backend
          const formData = new FormData();
          formData.append('file', file);
          
          const token = Cookies.get('auth_token');
          
          try {
            const result = await apiClient.post(`/menus/${processingId}/items/${itemId}/upload-image`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
            console.log('✅ Image uploaded with Redis sync:', result);
            
            // Update with the actual URL from backend
            setMenuItems(prev => prev.map(item =>
              item.id === itemId
                ? { 
                    ...item, 
                    uploaded_image_url: result.data.image_url,
                    ai_image_url: null,
                    imageStatus: 'user' as const,
                    aiGenerationEnabled: false
                  }
                : item
            ));
            
            toast({
              title: "Image Uploaded",
              description: "Your image has been uploaded and saved successfully",
            });
            
          } catch (uploadError) {
            // Revert on error
            console.error('Failed to upload image:', uploadError);
            
            // Fetch original item state to revert
            const originalItem = menuItems.find(item => item.id === itemId);
            if (originalItem) {
              setMenuItems(prev => prev.map(item =>
                item.id === itemId ? originalItem : item
              ));
            }
            
            toast({
              title: "Upload Failed",
              description: "Failed to save image. Please try again.",
              variant: "destructive"
            });
          }
        }
      };
      reader.readAsDataURL(file);
      
    } catch (error) {
      console.error('Error handling image upload:', error);
      toast({
        title: "Upload Error",
        description: "Failed to process image",
        variant: "destructive"
      });
    }
  };

  const handleBack = () => {
    if (isEditMode) {
      // In edit mode, go back to dashboard
      navigate('/restaurant-dashboard');
    } else {
      // In review mode, go back to sections
      navigate('/restaurant-menu-sections');
    }
  };

  const handleNext = () => {
    if (isEditMode) {
      // In edit mode, just go back to dashboard (changes are auto-saved)
      navigate('/restaurant-dashboard');
    } else {
      // In review mode, continue to publish flow
      navigate(`/restaurant-menu-publish?menu_id=${processingId}`);
    }
  };

  const ItemPreviewModal = ({ item, sectionName, onClose }: { item: MenuItem; sectionName?: string; onClose: () => void }) => {
    const isGenerating = aiGenerationStatus[item.id] === 'generating';
    const canRegenerate = aiMode && hasAIImages && item.ai_image_url && !item.uploaded_image_url && imageQuota.remaining > 0;
    
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-[#1f4ed1] rounded-2xl max-w-3xl w-full overflow-hidden shadow-2xl border border-white/20"
        >
          <div className="relative aspect-video">
            {item.uploaded_image_url || item.ai_image_url ? (
              <>
                <img
                  src={sanitizeImageUrl(item.uploaded_image_url || item.ai_image_url)}
                  alt={item.name}
                  className="w-full h-full object-cover"
                />
                {isGenerating && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="text-center">
                      <Loader2 className="h-12 w-12 text-white animate-spin mx-auto mb-3" />
                      <p className="text-white text-lg font-medium">Regenerating image...</p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                <Image className="h-24 w-24 text-gray-300" />
              </div>
            )}
          </div>
          <div className="p-8">
            <h3 className="text-2xl font-semibold text-white mb-3">{item.name}</h3>
            <p className="text-lg text-white/80 mb-6 leading-relaxed">{item.description}</p>
            <div className="flex justify-between items-center">
              <span className="text-3xl font-bold text-yellow-400">{getDisplayPrice(item, sectionName)}</span>
              <div className="flex items-center gap-3">
                {canRegenerate && (
                  <button
                    onClick={() => generateAIImage(item.id)}
                    disabled={isGenerating || imageQuota.remaining === 0}
                    className={cn(
                      "px-5 py-2.5 rounded-lg transition-all font-medium flex items-center gap-2 text-base",
                      isGenerating
                        ? "bg-yellow-100 text-yellow-700 cursor-not-allowed"
                        : imageQuota.remaining === 0
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-lg hover:shadow-xl"
                    )}
                    title={imageQuota.remaining === 0 ? "No quota remaining" : "Generate new AI image"}
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Regenerating
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-5 w-5" />
                        Regenerate
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="px-5 py-2.5 bg-white/10 text-white border border-white/20 rounded-lg hover:bg-white/20 transition-all font-medium text-base"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-white animate-spin mx-auto mb-4" />
          <p className="text-white text-xl">Loading menu data...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] flex items-center justify-center">
        <div className="text-center bg-white/10 backdrop-blur-md rounded-xl border border-white/20 p-8 max-w-md">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-white text-2xl font-semibold mb-2">Failed to Load Menu</h2>
          <p className="text-white/80 mb-4">{error}</p>
          <button
            onClick={() => navigate('/restaurant-dashboard')}
            className="px-6 py-2 bg-yellow-400 text-gray-900 rounded-lg hover:bg-yellow-300 transition-all font-medium"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Show empty state if no sections
  if (sections.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] flex items-center justify-center">
        <div className="text-center bg-white/10 backdrop-blur-md rounded-xl border border-white/20 p-8 max-w-md">
          <AlertCircle className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
          <h2 className="text-white text-2xl font-semibold mb-2">No Menu Data</h2>
          <p className="text-white/80 mb-4">This menu doesn't have any sections yet.</p>
          <button
            onClick={() => navigate('/restaurant-dashboard')}
            className="px-6 py-2 bg-yellow-400 text-gray-900 rounded-lg hover:bg-yellow-300 transition-all font-medium"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5]">
      {/* Standard Header - Clean and Simple */}
      <div className="border-b border-white/10 backdrop-blur-sm bg-white/5">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Dashboard
            </button>
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-semibold text-white">
                {aiMode ? 'AI Image Studio' : isEditMode ? 'Edit Menu' : 'Review Menu'}
              </h1>
              {aiMode && hasSeasonalMenus && (
                <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-1.5">
                  <span className="text-white/60 text-sm">Menu:</span>
                  <button
                    onClick={() => {
                      setIsSeasonalMode(false);
                      setIsInitialized(false); // Force data refresh
                    }}
                    className={`px-3 py-1 rounded text-sm font-medium transition-all ${
                      !isSeasonalMode 
                        ? 'bg-yellow-400 text-gray-900' 
                        : 'bg-white/10 text-white/60 hover:text-white'
                    }`}
                  >
                    Regular
                  </button>
                  <button
                    onClick={async () => {
                      // If we don't have a seasonal menu ID, try to fetch one
                      if (!activeSeasonalMenuId) {
                        try {
                          // Use apiClient for proper authentication
                          const response = await apiClient.get('/seasonal-menus');
                          
                          if (response.data) {
                            const data = response.data;
                            console.log('🔍 Fetching seasonal menu on click:', data);
                            
                            // Use proper backend response structure
                            const seasonalMenus = Array.isArray(data) ? data : data.seasonal_menus || [];
                            
                            if (seasonalMenus.length > 0) {
                              // Find the most recently created active seasonal menu
                              const activeMenu = seasonalMenus
                                .filter((menu: any) => menu.is_active)
                                .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0] 
                                || seasonalMenus[0]; // Fallback to first menu if none are active
                              
                              if (activeMenu) {
                                setActiveSeasonalMenuId(activeMenu.seasonal_menu_id.toString());
                                console.log('✅ Set seasonal menu ID:', activeMenu.seasonal_menu_id);
                              }
                            }
                          }
                        } catch (error) {
                          console.error('Failed to fetch seasonal menu:', error);
                        }
                      }
                      
                      setIsSeasonalMode(true);
                      setIsInitialized(false); // Force data refresh
                    }}
                    className={`px-3 py-1 rounded text-sm font-medium transition-all ${
                      isSeasonalMode 
                        ? 'bg-yellow-400 text-gray-900' 
                        : 'bg-white/10 text-white/60 hover:text-white'
                    }`}
                    title='Switch to seasonal menu'
                  >
                    Seasonal
                  </button>
                </div>
              )}
            </div>
            <div className="w-20" /> {/* Spacer for centering */}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 pb-32">{/* Removed extra wrapper divs and motion animation */}

        {/* Progress Bar - Only show in review mode */}
        {!isEditMode && (
          <div className="max-w-3xl mx-auto mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-full h-2">
              <motion.div
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className="h-full bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full"
              />
            </div>
            <p className="text-center text-white/80 mt-2">Final Step</p>
          </div>
        )}

        {/* Persistent Quick Win Progress (shows in all modes when there are active wins) */}
        {showQuickWinProgress && activeQuickWins.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-7xl mx-auto mb-6"
          >
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-400/20 rounded-lg">
                    <Target className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">Menu Updates</h3>
                    <p className="text-white/60 text-sm">Making changes to improve your menu</p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {activeQuickWins.map((win) => {
                    const isPhotoWin = win.category === 'photos';
                    const relevantItems = menuItems.filter(item => 
                      isPhotoWin 
                        ? item.imageStatus === 'none'
                        : !item.description || item.description.trim() === ''
                    );
                    const totalItems = relevantItems.length;
                    const completedItems = menuItems.filter(item => 
                      isPhotoWin 
                        ? item.imageStatus !== 'none'
                        : item.description && item.description.trim() !== ''
                    ).length;
                    const originalTotal = completedItems + totalItems;
                    const progress = originalTotal > 0 ? (completedItems / originalTotal) * 100 : 0;
                    
                    return (
                      <div key={win.id} className="text-right">
                        <p className="text-sm text-white/80">
                          {win.title}: <span className="text-yellow-300 font-medium">{completedItems}/{originalTotal}</span>
                        </p>
                        <div className="w-32 h-2 bg-white/20 rounded-full overflow-hidden mt-1">
                          <div 
                            className="h-full bg-gradient-to-r from-yellow-400 to-green-400 transition-all duration-500"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  <button
                    onClick={() => {
                      const firstWin = activeQuickWins[0];
                      if (firstWin && firstWin.action_url) {
                        navigate(firstWin.action_url);
                      }
                    }}
                    className="text-xs text-yellow-400 hover:text-yellow-300 transition-colors"
                  >
                    View in Quick Win mode →
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Quick Win Mode Banner */}
        {isQuickWinMode && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-7xl mx-auto mb-8"
          >
            <div className="bg-yellow-400/20 backdrop-blur-md rounded-xl p-6 border border-yellow-400/30">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-400/30 rounded-lg">
                  <Target className="h-8 w-8 text-yellow-300" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-semibold text-white mb-1">
                    Quick Win Mode: {quickWinType === 'photos' ? 'Add Photos' : 'Complete Descriptions'}
                  </h2>
                  <p className="text-white/80 mb-3">
                    {quickWinType === 'photos' 
                      ? `Adding photos can increase sales by 30%!`
                      : `Detailed descriptions increase order value by 15%!`
                    }
                  </p>
                  
                  {/* Progress Bar */}
                  {(() => {
                    // Calculate progress from ALL menu items, not just filtered ones
                    const allRelevantItems = menuItems.filter(item => 
                      quickWinType === 'photos' 
                        ? true  // Count all items for photos
                        : true  // Count all items for descriptions
                    );
                    const itemsNeedingWork = allRelevantItems.filter(item => 
                      quickWinType === 'photos' 
                        ? item.imageStatus === 'none' 
                        : !item.description || item.description.trim() === ''
                    );
                    const completedItems = allRelevantItems.filter(item => 
                      quickWinType === 'photos' 
                        ? item.imageStatus !== 'none' 
                        : item.description && item.description.trim() !== ''
                    );
                    const totalItems = allRelevantItems.length;
                    const progress = totalItems > 0 ? (completedItems.length / totalItems) * 100 : 0;
                    const isComplete = itemsNeedingWork.length === 0 && totalItems > 0;
                    
                    return (
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-white/80">Progress</span>
                          <span className="text-yellow-300 font-medium">
                            {completedItems.length} of {totalItems} completed
                          </span>
                        </div>
                        <div className="w-full bg-white/20 rounded-full h-3 overflow-hidden">
                          <motion.div
                            className="h-full bg-gradient-to-r from-yellow-400 to-green-400 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${progress}%` }}
                            transition={{ duration: 0.5, ease: "easeOut" }}
                          />
                        </div>
                        {isComplete && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-green-300 text-sm mt-2 font-medium"
                          >
                            ✨ Excellent! All items now have {quickWinType === 'photos' ? 'photos' : 'descriptions'}!
                          </motion.p>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <button
                  onClick={async () => {
                    // Use the same calculation as the progress bar
                    const allRelevantItems = menuItems.filter(item => 
                      quickWinType === 'photos' 
                        ? true  // Count all items for photos
                        : true  // Count all items for descriptions
                    );
                    const itemsNeedingWork = allRelevantItems.filter(item => 
                      quickWinType === 'photos' 
                        ? item.imageStatus === 'none' 
                        : !item.description || item.description.trim() === ''
                    );
                    const completedCount = allRelevantItems.filter(item => 
                      quickWinType === 'photos' 
                        ? item.imageStatus !== 'none' 
                        : item.description && item.description.trim() !== ''
                    ).length;
                    const totalItems = allRelevantItems.length;
                    const isComplete = itemsNeedingWork.length === 0 && totalItems > 0;
                    
                    if (isComplete) {
                      // Get the Quick Win ID from URL params
                      const quickWinId = searchParams.get('quickwin_id');
                      if (quickWinId) {
                        try {
                          // Call API to mark Quick Win as completed
                          // Quick wins feature is disabled
                          // await apiClient.put(`/quick-wins/${quickWinId}/complete`);
                          setQuickWinCompleted(true);
                          setTimeout(() => navigate('/analytics'), 1500);
                        } catch (error) {
                          toast({
                            title: "Failed to complete Quick Win",
                            description: "Please try again",
                            variant: "destructive",
                          });
                        }
                      }
                    } else {
                      toast({
                        title: "Not quite done yet!",
                        description: `Please complete all ${itemsNeedingWork.length} remaining items first.`,
                        variant: "destructive",
                      });
                    }
                  }}
                  disabled={(() => {
                    const allRelevantItems = menuItems.filter(item => 
                      quickWinType === 'photos' 
                        ? true  // Count all items for photos
                        : true  // Count all items for descriptions
                    );
                    const itemsNeedingWork = allRelevantItems.filter(item => 
                      quickWinType === 'photos' 
                        ? item.imageStatus === 'none' 
                        : !item.description || item.description.trim() === ''
                    );
                    return itemsNeedingWork.length > 0 || allRelevantItems.length === 0;
                  })()}
                  className={cn(
                    "px-6 py-3 rounded-lg transition-all font-medium flex items-center gap-2",
                    (() => {
                      const allRelevantItems = menuItems.filter(item => 
                        quickWinType === 'photos' 
                          ? true  // Count all items for photos
                          : true  // Count all items for descriptions
                      );
                      const itemsNeedingWork = allRelevantItems.filter(item => 
                        quickWinType === 'photos' 
                          ? item.imageStatus === 'none' 
                          : !item.description || item.description.trim() === ''
                      );
                      const isComplete = itemsNeedingWork.length === 0 && allRelevantItems.length > 0;
                      
                      return isComplete
                        ? "bg-green-500 text-white hover:bg-green-600"
                        : "bg-gray-500/50 text-gray-300 cursor-not-allowed";
                    })()
                  )}
                >
                  <CheckCircle className="h-5 w-5" />
                  Complete Quick Win
                </button>
              </div>
              {quickWinCompleted && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mt-4 p-4 bg-green-500/20 rounded-lg border border-green-500/30"
                >
                  <p className="text-green-300 text-center font-medium">
                    🎉 Great job! Quick Win completed. Returning to Analytics...
                  </p>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}


        {/* Search Bar */}
        {!isQuickWinMode && (
          <div className="max-w-7xl mx-auto mb-6">
            <MenuSearch
              onSearch={handleSearch}
              sections={sections.map(s => ({ id: s.id, name: getSectionName(s.id) }))}
              totalItems={menuItems.length}
              filteredItems={filteredItems.length}
            />
          </div>
        )}

        {/* Main Content - New Layout */}
        <div className="max-w-7xl mx-auto flex gap-6">
          {/* Left Sidebar - Section Navigation */}
          <div className="w-72 flex-shrink-0">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20 sticky top-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-white">Menu Sections</h3>
                <button
                  onClick={() => setCreatingSection(true)}
                  className="p-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded-lg transition-all"
                  title="Add New Section"
                >
                  <FolderPlus className="w-4 h-4 text-green-400" />
                </button>
              </div>
              
              {/* Create New Section Form */}
              {creatingSection && (
                <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <input
                    type="text"
                    value={newSectionName}
                    onChange={(e) => setNewSectionName(e.target.value)}
                    placeholder="Section name (e.g., 'Seasonal Specials')"
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/60 focus:outline-none focus:border-yellow-400/50 mb-2"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateSection();
                      if (e.key === 'Escape') { setCreatingSection(false); setNewSectionName(''); }
                    }}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateSection}
                      className="flex-1 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 text-green-400 py-2 px-3 rounded-lg text-sm font-medium transition-all"
                    >
                      Create
                    </button>
                    <button
                      onClick={() => { setCreatingSection(false); setNewSectionName(''); }}
                      className="flex-1 bg-gray-500/20 hover:bg-gray-500/30 border border-gray-500/30 text-gray-400 py-2 px-3 rounded-lg text-sm font-medium transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                {sectionsWithItems.map((section) => (
                  <div
                    key={section.id}
                    className={`rounded-lg transition-all border relative ${
                      selectedSectionId === section.id
                        ? 'bg-yellow-400/20 border-yellow-400/40 text-white shadow-lg'
                        : 'bg-white/5 hover:bg-white/10 border-white/10 hover:border-white/20 text-white/90'
                    }`}
                  >
                    {selectedSectionId === section.id && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-yellow-400 rounded-l-lg"></div>
                    )}
                    
                    {/* Section Name - Editable */}
                    {editingSections[section.id] !== undefined ? (
                      <div className="p-3">
                        <input
                          type="text"
                          value={editingSections[section.id]}
                          onChange={(e) => setEditingSections({
                            ...editingSections,
                            [section.id]: e.target.value
                          })}
                          className="w-full bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm mb-2"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveSection(section.id);
                            if (e.key === 'Escape') handleCancelEditSection(section.id);
                          }}
                        />
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleSaveSection(section.id)}
                            className="p-1 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded text-green-400 transition-all"
                            title="Save section name"
                          >
                            <Save className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => handleCancelEditSection(section.id)}
                            className="p-1 bg-gray-500/20 hover:bg-gray-500/30 border border-gray-500/30 rounded text-gray-400 transition-all"
                            title="Cancel editing"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex">
                        <button
                          onClick={() => setSelectedSectionId(section.id)}
                          className="flex-1 text-left p-3"
                        >
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-sm">{section.name}</span>
                            <span className={`text-xs ${selectedSectionId === section.id ? 'text-yellow-300' : 'text-white/60'}`}>
                              {section.items.length}
                            </span>
                          </div>
                        </button>
                        
                        {/* Section Controls */}
                        <div className="flex items-center gap-1 pr-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartEditSection(section.id, section.name);
                            }}
                            className="p-1 opacity-60 hover:opacity-100 hover:bg-white/10 rounded transition-all"
                            title="Rename section"
                          >
                            <Pen className="w-3 h-3" />
                          </button>
                          {section.items.length === 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSection(section.id);
                              }}
                              className="p-1 opacity-60 hover:opacity-100 hover:bg-red-500/20 rounded transition-all text-red-400"
                              title="Delete empty section"
                            >
                              <FolderX className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Content - Selected Section Only */}
          <div className="flex-1">
            {selectedSection && (
              <motion.div
                key={selectedSection.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="mb-8"
              >
                <div className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 overflow-hidden">
                  {/* Section Header */}
                  <div className="bg-white/5 px-6 py-4 border-b border-white/10">
                    <div className="flex justify-between items-center">
                      <div>
                        <h2 className="text-2xl font-semibold text-white">{selectedSection.name}</h2>
                        <p className="text-white/60 text-sm">{selectedSection.items.length} items</p>
                      </div>
                      <div className="flex items-center gap-3">
                        {/* AI Generation Button - Only show in AI mode */}
                        {aiMode && hasAIImages && (
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => {
                                console.log('Generate clicked, selected items:', selectedItemsForAI);
                                console.log('Items in section:', menuItems.filter(i => i.section_id === selectedSection.id));
                                generateSectionImages(selectedSection.id);
                              }}
                              disabled={(() => {
                                // Disable if currently generating
                                if (sectionGenerationStatus[selectedSection.id]) {
                                  console.log('🔴 Button disabled: Currently generating');
                                  return true;
                                }
                                // Disable if no quota
                                if (imageQuota.remaining === 0) {
                                  console.log('🔴 Button disabled: No quota remaining');
                                  return true;
                                }
                                
                                console.log('🔍 Checking button state:', {
                                  currentSectionId: selectedSection.id,
                                  currentSectionName: selectedSection.name,
                                  selectedItemsSet: Array.from(selectedItemsForAI),
                                  imageQuota,
                                  imageQuotaRemaining: imageQuota.remaining,
                                  isQuotaZero: imageQuota.remaining === 0
                                });
                                
                                // Check if there are selected items in this section that need images
                                const selectedItemsNeedingImages = menuItems.filter(item => {
                                  const isInSection = item.section_id === selectedSection.id;
                                  const itemIdStr = String(item.id);
                                  const isSelected = selectedItemsForAI.has(itemIdStr);
                                  const needsImage = item.imageStatus === 'none';
                                  
                                  if (isInSection && needsImage) {
                                    console.log('📋 Item in section needing image:', {
                                      itemId: item.id,
                                      itemIdStr: itemIdStr,
                                      itemName: item.name,
                                      isSelected: isSelected,
                                      selectedItemsForAI: Array.from(selectedItemsForAI),
                                      setHasItem: selectedItemsForAI.has(itemIdStr)
                                    });
                                  }
                                  
                                  return isInSection && isSelected && needsImage;
                                }).length;
                                
                                // Check if there are any items without images
                                const itemsWithoutImages = menuItems.filter(item => 
                                  item.section_id === selectedSection.id && 
                                  item.imageStatus === 'none'
                                ).length;
                                
                                // Enable if there are selected items that need images OR items without images
                                const shouldEnable = selectedItemsNeedingImages > 0 || itemsWithoutImages > 0;
                                
                                console.log('🎯 Final button state:', {
                                  selectedItemsNeedingImages,
                                  itemsWithoutImages,
                                  shouldEnable,
                                  buttonWillBeDisabled: !shouldEnable,
                                  selectedItemsForAI: Array.from(selectedItemsForAI),
                                  sectionId: selectedSection.id
                                });
                                
                                return !shouldEnable;
                              })()}
                              className={cn(
                                "flex items-center gap-2",
                                imageQuota.remaining === 0
                                  ? "bg-gray-600 hover:bg-gray-700 text-gray-300 cursor-not-allowed"
                                  : "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                              )}
                            >
                              {sectionGenerationStatus[selectedSection.id] ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="h-4 w-4" />
                                  {(() => {
                                    const selectedCount = menuItems.filter(item => 
                                      item.section_id === selectedSection.id && 
                                      selectedItemsForAI.has(String(item.id))
                                    ).length;
                                    if (imageQuota.remaining === 0) {
                                      return 'No quota remaining';
                                    }
                                    const buttonText = selectedCount > 0 
                                      ? `Generate ${selectedCount} AI Images`
                                      : 'Generate AI Images';
                                    return `${buttonText} (${imageQuota.remaining} left)`;
                                  })()}
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                        <button
                          onClick={() => {
                            console.log('🔘 Add Item button clicked!');
                            console.log('🔘 selectedSection:', selectedSection);
                            console.log('🔘 creatingItem state:', creatingItem);
                            handleAddItem(selectedSection.id);
                          }}
                          disabled={creatingItem}
                          className="flex items-center gap-2 px-4 py-2 bg-yellow-400 text-gray-900 font-medium rounded-lg hover:bg-yellow-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {creatingItem ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <Plus className="h-4 w-4" />
                          )}
                          {creatingItem ? 'Creating...' : 'Add Item'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items - Clean Menu Style */}
                  <div className="p-6">
                    <div>
                      {selectedSection.items.map((item) => {
                        const isEditing = !!editingItems[item.id];
                        const editingItem = editingItems[item.id] || item;

                        const needsAttention = isQuickWinMode && (
                          (quickWinFilter === 'no-photos' && !item.uploaded_image_url && !item.ai_image_url) ||
                          (quickWinFilter === 'no-description' && (!item.description || item.description.trim() === ''))
                        );

                        return (
                          <div
                            id={`menu-item-${item.id}`}
                            key={item.id}
                            className={cn(
                              "group relative rounded-lg transition-all duration-300 mb-3",
                              needsAttention 
                                ? "bg-yellow-400/10 border-2 border-yellow-400/40 hover:bg-yellow-400/20 hover:border-yellow-400/50"
                                : "bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#1f4ed1]/40"
                            )}
                          >
                            {isEditing ? (
                              <div className="relative z-10 p-4 bg-white/10 rounded-lg border border-white/20">
                                <div className="space-y-3">
                                  <input
                                    type="text"
                                    value={editingItem.name || ''}
                                    onChange={(e) => handleFieldChange(item.id, 'name', e.target.value)}
                                    placeholder="Item name"
                                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-white/40 font-medium"
                                  />
                                  <textarea
                                    value={editingItem.description || ''}
                                    onChange={(e) => handleFieldChange(item.id, 'description', e.target.value)}
                                    placeholder="Description"
                                    rows={2}
                                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-white/40 resize-none"
                                  />
                                  {/* Section Dropdown */}
                                  <div>
                                    <label className="text-white/60 text-sm block mb-1">Section</label>
                                    <select
                                      value={editingItem.section_id || item.section_id}
                                      onChange={(e) => {
                                        const newSectionId = e.target.value;
                                        const newSection = sections.find(s => s.id === newSectionId);
                                        if (newSection) {
                                          handleFieldChange(item.id, 'section_id', newSectionId);
                                          handleFieldChange(item.id, 'section', newSection.name);
                                        }
                                      }}
                                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/40"
                                    >
                                      {sections.map(section => (
                                        <option key={section.id} value={section.id} className="bg-gray-800">
                                          {section.name}
                                        </option>
                                      ))}
                                    </select>
                                    {editingItem.section !== item.section && (
                                      <p className="text-yellow-400 text-xs mt-1">
                                        Moving from "{selectedSection.name}" to "{editingItem.section}" will trigger re-categorization
                                      </p>
                                    )}
                                  </div>
                                  {/* Pricing Type Toggles */}
                                  <div className="space-y-2 mb-3">
                                    {/* Dual Pricing Toggle - Only show for beverage sections */}
                                    {isBeverageSection(selectedSection.name) && (
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          id={`wine-toggle-${item.id}`}
                                          checked={isWineItem(editingItem) && !editingItem.is_market_price}
                                          onChange={(e) => handleWineToggle(item.id, e.target.checked)}
                                          disabled={editingItem.is_market_price}
                                          className="w-4 h-4 text-yellow-400 bg-white/10 border-white/20 rounded focus:ring-yellow-400 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        />
                                        <label htmlFor={`wine-toggle-${item.id}`} className={`text-sm ${
                                          editingItem.is_market_price ? 'text-white/40' : 'text-white/80'
                                        }`}>
                                          Glass & Bottle Pricing
                                        </label>
                                      </div>
                                    )}

                                    {/* Market Price Toggle */}
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        id={`market-toggle-${item.id}`}
                                        checked={editingItem.is_market_price || false}
                                        onChange={(e) => handleMarketPriceToggle(item.id, e.target.checked)}
                                        disabled={isWineItem(editingItem) && !editingItem.is_market_price}
                                        className="w-4 h-4 text-yellow-400 bg-white/10 border-white/20 rounded focus:ring-yellow-400 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                      />
                                      <label htmlFor={`market-toggle-${item.id}`} className={`text-sm ${
                                        isWineItem(editingItem) && !editingItem.is_market_price ? 'text-white/40' : 'text-white/80'
                                      }`}>
                                        Market Price - Variable Pricing
                                      </label>
                                    </div>
                                  </div>

                                  {/* Price Inputs */}
                                  {editingItem.is_market_price ? (
                                    /* Market Price Inputs */
                                    <div>
                                      <label className="text-white/60 text-sm block mb-1">Market Price Display</label>
                                      <input
                                        type="text"
                                        value={editingItem.market_price_indicator || 'Market Price'}
                                        onChange={(e) => handleFieldChange(item.id, 'market_price_indicator', e.target.value)}
                                        placeholder="e.g., MKT, Market Price, Seasonal"
                                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-white/40"
                                      />
                                      <p className="text-white/50 text-xs mt-1">This text will appear instead of a price (e.g., "MKT" or "Market Price")</p>
                                    </div>
                                  ) : isWineItem(editingItem) ? (
                                    /* Dual Pricing */
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <label className="text-white/60 text-sm block mb-1">
                                          Glass Price
                                        </label>
                                        <div className="flex gap-2 items-center">
                                          <span className="text-white/60 text-sm">$</span>
                                          <input
                                            type="number"
                                            step="0.01"
                                            value={editingItem.price || ''}
                                            onChange={(e) => handleFieldChange(item.id, 'price', e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
                                            placeholder="0.00"
                                            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-white/40"
                                          />
                                        </div>
                                      </div>
                                      <div>
                                        <label className="text-white/60 text-sm block mb-1">
                                          Bottle Price
                                        </label>
                                        <div className="flex gap-2 items-center">
                                          <span className="text-white/60 text-sm">$</span>
                                          <input
                                            type="number"
                                            step="0.01"
                                            value={editingItem.additional_price || ''}
                                            onChange={(e) => handleFieldChange(item.id, 'additional_price', e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
                                            placeholder="0.00"
                                            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-white/40"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    /* Regular Fixed Pricing */
                                    <div>
                                      <label className="text-white/60 text-sm block mb-1">Price</label>
                                      <div className="flex gap-2 items-center">
                                        <span className="text-white/60 text-sm">$</span>
                                        <input
                                          type="number"
                                          step="0.01"
                                          value={editingItem.price || ''}
                                          onChange={(e) => handleFieldChange(item.id, 'price', e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
                                          placeholder="0.00"
                                          className="w-24 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-white/40"
                                        />
                                      </div>
                                    </div>
                                  )}
                                  <div className="flex gap-2 ml-auto">
                                    <button
                                      onClick={() => handleSave(item.id)}
                                      disabled={creatingItem}
                                      className="p-2 bg-green-500/20 border border-green-500/30 text-green-300 rounded-lg hover:bg-green-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                      title="Save changes"
                                    >
                                      {creatingItem ? (
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <Save className="h-4 w-4" />
                                      )}
                                    </button>
                                    <button
                                      onClick={() => handleCancel(item.id)}
                                      className="p-2 bg-red-500/20 border border-red-500/30 text-red-300 rounded-lg hover:bg-red-500/30 transition-all"
                                      title="Cancel"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="p-4">
                                {/* Quick Win Indicator */}
                                {needsAttention && (
                                  <div className="mb-3 flex items-center gap-2 p-2 bg-yellow-400/20 rounded-lg border border-yellow-400/30">
                                    <AlertCircle className="h-4 w-4 text-yellow-300" />
                                    <span className="text-yellow-300 text-sm font-medium">
                                      {quickWinFilter === 'no-photos' ? 'Needs Photo' : 'Needs Description'}
                                    </span>
                                  </div>
                                )}
                                
                                <div className="flex justify-between items-start mb-1">
                                  <div className="flex items-start gap-3">
                                    {/* Checkbox for bulk AI generation - only show in AI mode */}
                                    {aiMode && hasAIImages && item.imageStatus === 'none' && (
                                      <input
                                        type="checkbox"
                                        checked={selectedItemsForAI.has(item.id)}
                                        onChange={(e) => {
                                          const newSelected = new Set(selectedItemsForAI);
                                          const itemIdStr = String(item.id);
                                          if (e.target.checked) {
                                            newSelected.add(itemIdStr);
                                          } else {
                                            newSelected.delete(itemIdStr);
                                          }
                                          console.log('🔲 Checkbox changed:', {
                                            itemId: item.id,
                                            itemIdStr: itemIdStr,
                                            itemIdType: typeof item.id,
                                            checked: e.target.checked,
                                            selectedItems: Array.from(newSelected),
                                            sectionId: item.section_id
                                          });
                                          setSelectedItemsForAI(newSelected);
                                        }}
                                        className="w-4 h-4 mt-2 text-purple-500 bg-white/10 border-white/20 rounded focus:ring-purple-500 focus:ring-2 cursor-pointer"
                                        disabled={aiGenerationStatus[item.id] === 'generating'}
                                      />
                                    )}
                                    {/* Image Status Indicator */}
                                    <div className={cn(
                                      "flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0",
                                      item.imageStatus === 'user' ? "bg-green-500/20 border border-green-500/30" :
                                      item.imageStatus === 'ai' ? "bg-purple-500/20 border border-purple-500/30" :
                                      aiGenerationStatus[item.id] === 'generating' ? "bg-yellow-500/20 border border-yellow-500/30" :
                                      "bg-gray-500/20 border border-gray-500/30"
                                    )}>
                                      {aiGenerationStatus[item.id] === 'generating' ? (
                                        <Loader2 className="h-4 w-4 text-yellow-400 animate-spin" />
                                      ) : item.imageStatus === 'user' ? (
                                        <CheckCircle className="h-4 w-4 text-green-400" />
                                      ) : item.imageStatus === 'ai' ? (
                                        <Sparkles className="h-4 w-4 text-purple-400" />
                                      ) : (
                                        <Image className="h-4 w-4 text-gray-400" />
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <h3 className="text-lg font-semibold text-white leading-tight">{item.name}</h3>
                                      {item.imageStatus && (
                                        <p className={cn(
                                          "text-xs mt-0.5",
                                          item.imageStatus === 'user' ? "text-green-400" :
                                          item.imageStatus === 'ai' ? "text-purple-400" :
                                          aiGenerationStatus[item.id] === 'generating' ? "text-yellow-400" :
                                          "text-gray-400"
                                        )}>
                                          {aiGenerationStatus[item.id] === 'generating' ? 'Generating AI image...' :
                                           item.imageStatus === 'user' ? 'User uploaded' :
                                           item.imageStatus === 'ai' ? 'AI generated' :
                                           'No image'}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <span className="text-yellow-400 font-bold text-lg flex-shrink-0">{getDisplayPrice(item, selectedSection.name)}</span>
                                </div>
                                {item.description && (
                                  <p className="text-white/75 text-sm leading-relaxed mb-3 ml-11">{item.description}</p>
                                )}

                                <div className="flex gap-2 pt-3 mt-3 border-t border-white/10">
                                  {!isReadOnlyMode && (
                                    <Button
                                      onClick={() => setEditingItems({...editingItems, [item.id]: item})}
                                      variant="ghost"
                                      className="text-green-400/60 hover:text-green-400 hover:bg-green-500/10"
                                      size="sm"
                                      title="Edit item"
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                  
                                  <Button
                                    onClick={() => setShowItemPreview(item.id)}
                                    variant="ghost"
                                    className="text-blue-400/60 hover:text-blue-400 hover:bg-blue-500/10"
                                    size="sm"
                                    title="Preview item"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  
                                  <label className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-purple-500/10 hover:text-purple-400 h-8 w-8 text-purple-400/60 cursor-pointer"
                                    title="Upload image"
                                  >
                                    <Upload className="h-4 w-4" />
                                    <input
                                      type="file"
                                      accept=".jpg,.jpeg,.png"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                          // Client-side validation
                                          const maxSize = 5 * 1024 * 1024; // 5MB
                                          if (file.size > maxSize) {
                                            toast({
                                              title: "File too large",
                                              description: "Maximum file size is 5MB. Please choose a smaller image.",
                                              variant: "destructive"
                                            });
                                            e.target.value = ''; // Reset input
                                            return;
                                          }
                                          
                                          const allowedTypes = ['image/jpeg', 'image/png'];
                                          if (!allowedTypes.includes(file.type)) {
                                            toast({
                                              title: "Invalid file type",
                                              description: "Only JPEG and PNG images are allowed.",
                                              variant: "destructive"
                                            });
                                            e.target.value = ''; // Reset input
                                            return;
                                          }
                                          
                                          handleImageUpload(item.id, file);
                                        }
                                      }}
                                      className="hidden"
                                    />
                                  </label>
                                  
                                  {/* AI Image Generation Button - Show in AI mode or Quick Win photo mode */}
                                  {((aiMode && hasAIImages) || (isQuickWinMode && quickWinType === 'photos')) && item.imageStatus === 'none' && hasAIImages && (
                                    <Button
                                      onClick={() => generateAIImage(item.id)}
                                      disabled={aiGenerationStatus[item.id] === 'generating' || imageQuota.remaining === 0}
                                      variant="ghost"
                                      className={cn(
                                        aiGenerationStatus[item.id] === 'generating' 
                                          ? "text-yellow-400/60 hover:text-yellow-400 hover:bg-yellow-500/10"
                                          : imageQuota.remaining === 0
                                          ? "text-gray-400/60 cursor-not-allowed"
                                          : "text-purple-400/60 hover:text-purple-400 hover:bg-purple-500/10"
                                      )}
                                      size="sm"
                                      title={imageQuota.remaining === 0 ? "No AI images remaining" : "Generate AI image"}
                                    >
                                      {aiGenerationStatus[item.id] === 'generating' ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <Sparkles className="h-4 w-4" />
                                      )}
                                    </Button>
                                  )}
                                  
                                  {/* Regenerate AI Image Button - Show when item has AI image */}
                                  {aiMode && hasAIImages && item.imageStatus === 'ai' && imageQuota.remaining > 0 && (
                                    <Button
                                      onClick={() => generateAIImage(item.id)}
                                      disabled={aiGenerationStatus[item.id] === 'generating'}
                                      variant="ghost"
                                      className="text-blue-400/60 hover:text-blue-400 hover:bg-blue-500/10"
                                      size="sm"
                                      title="Regenerate AI image"
                                    >
                                      {aiGenerationStatus[item.id] === 'generating' ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <RefreshCw className="h-4 w-4" />
                                      )}
                                    </Button>
                                  )}
                                  
                                  {/* Clear Image Button - Show when item has any image */}
                                  {item.imageStatus !== 'none' && (
                                    <Button
                                      onClick={() => handleClearImage(item.id)}
                                      variant="ghost"
                                      className="text-red-400/60 hover:text-red-400 hover:bg-red-500/10"
                                      size="sm"
                                      title="Clear image"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  )}
                                  
                                  <div className="flex-1" /> {/* Spacer */}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Sticky Bottom Bar - Clean and Modern like Seasonal Menu */}
        {(isEditMode || aiMode) && (
          <div className="fixed bottom-0 left-0 right-0 bg-black/80 backdrop-blur-md border-t border-white/20 p-4 z-40">
            <div className="container mx-auto max-w-6xl flex justify-between items-center">
              <p className="text-white/60 text-sm">
                {menuItems.length} items in your {isSeasonalMode ? 'seasonal' : 'regular'} menu
                {aiMode && hasSeasonalMenus && (
                  <span className="ml-2 text-yellow-400">
                    ({isSeasonalMode ? 'Seasonal' : 'Regular'} menu selected)
                  </span>
                )}
              </p>
              {!aiMode && (
                <Button
                  onClick={handleNext}
                  className="bg-yellow-400 text-black hover:bg-yellow-300 font-bold px-8 py-3 rounded-full shadow-lg transform hover:scale-105 transition-all flex items-center gap-2"
                >
                  <CheckCircle className="h-5 w-5" />
                  Save & Finish
                </Button>
              )}
            </div>
          </div>
        )}
        
        {/* Review Mode - Keep existing publish bar */}
        {!isEditMode && !aiMode && (
          <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-[#1f4ed1] to-transparent pb-8 pt-16 z-40">
            <div className="container mx-auto px-4">
              <div className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white font-medium">Ready to go live?</p>
                    <p className="text-white/60 text-sm">Your menu will be available to customers immediately</p>
                  </div>
                  <button
                    onClick={handleNext}
                    className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 font-medium rounded-lg hover:from-yellow-500 hover:to-yellow-600 transition-all shadow-lg"
                  >
                    Publish Menu
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Item Preview Modal */}
      {showItemPreview && (
        <ItemPreviewModal
          item={menuItems.find(i => i.id === showItemPreview)!}
          sectionName={selectedSection?.name}
          onClose={() => setShowItemPreview(null)}
        />
      )}


      {/* Add Item Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-[#1f4ed1] border-white/20">
          <DialogHeader>
            <DialogTitle className="text-white">Add Menu Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-white/80">Item Name *</label>
              <Input
                value={newItem.item_name}
                onChange={(e) => setNewItem({ ...newItem, item_name: e.target.value })}
                placeholder="Enter item name"
                className="mt-1 bg-white/10 border-white/20 text-white placeholder-white/50"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white/80">Description</label>
              <Textarea
                value={newItem.item_description}
                onChange={(e) => setNewItem({ ...newItem, item_description: e.target.value })}
                placeholder="Enter item description"
                className="mt-1 bg-white/10 border-white/20 text-white placeholder-white/50"
                rows={3}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white/80">Price *</label>
              <Input
                type="number"
                step="0.01"
                value={newItem.primary_price}
                onChange={(e) => setNewItem({ ...newItem, primary_price: e.target.value })}
                placeholder="0.00"
                className="mt-1 bg-white/10 border-white/20 text-white placeholder-white/50"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white/80">Section *</label>
              <Select
                value={newItem.section}
                onValueChange={(value) => setNewItem({ ...newItem, section: value })}
              >
                <SelectTrigger className="mt-1 bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Select a section" />
                </SelectTrigger>
                <SelectContent className="bg-[#1f4ed1] border-white/20">
                  {sections.map((section) => (
                    <SelectItem 
                      key={section.id} 
                      value={section.name}
                      className="text-white hover:bg-white/10 focus:bg-white/20"
                    >
                      {section.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddModal(false);
                setNewItem({
                  item_name: '',
                  item_description: '',
                  primary_price: '10.00',
                  section: ''
                });
              }}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateItem}
              disabled={creatingItem}
              className="bg-yellow-400 text-gray-900 hover:bg-yellow-300 font-medium"
            >
              {creatingItem ? 'Adding...' : 'Add Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RestaurantMenuPreview;